import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Divider
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { postRequest, getRequest, putRequest } from 'Includes/api/apicall';
import { POST_URL, GET_URL, PUT_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { validateMobileNumberFormat } from 'Includes/functions';
import { useNavigate } from 'react-router-dom';
function UserAdd(props) {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const editUserId = queryParams.get('id');
  const isEditMode = Boolean(editUserId);
  const [loading, setLoading] = React.useState(false);
  const [submitDisable, setSubmitDisable] = React.useState(false);

  const [form, setForm] = React.useState({
    username: '',
    password: '',
    email: '',
    mobile_num: '',
    is_active: true,
    is_staff: false,
    is_superuser: false,
    groups: [],
  });

  const [formErrors, setFormErrors] = React.useState({});
  const [groupsList, setGroupsList] = React.useState([]);

  React.useEffect(() => {
    getGroupsList();
  }, []);

  React.useEffect(() => {
    if (!isEditMode || !editUserId) return;
    setLoading(true);
    getRequest(`${GET_URL.user.api}${editUserId}/`, {}, props)
      .then((response) => {
        if (response?.status === 200) {
          const userData =
            response.data?.data?.user ||
            response.data?.data ||
            response.data ||
            {};
          setForm((prev) => ({
            ...prev,
            username: userData.username || '',
            email: userData.email || '',
            mobile_num: userData.mobile_num || '',
            is_active: userData.is_active !== false,
            is_staff: Boolean(userData.is_staff),
            is_superuser: Boolean(userData.is_superuser),
            groups: Array.isArray(userData.groups)
              ? userData.groups
                  .map((g) => {
                    const raw = typeof g === 'object' && g != null ? g.id : g;
                    const n = Number(raw);
                    return Number.isNaN(n) ? null : n;
                  })
                  .filter((id) => id != null)
              : [],
            password: '',
          }));
        }
      })
      .finally(() => setLoading(false));
  }, [editUserId, isEditMode]);

  const getGroupsList = () => {
    getRequest(GET_URL.groups.api, {}, props)
      .then(response => {
        if (response?.status === 200) {
          const groups = response.data.data || response.data || [];
          setGroupsList(groups.filter(g => g.id !== 1));
        }
      })
      .catch(() => setGroupsList([]));
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setForm(prev => ({ ...prev, [name]: val }));

    if (formErrors[name]) {
      setFormErrors(prev => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleGroupsChange = (event) => {
    const value = event.target.value;
    const raw = typeof value === 'string' ? value.split(',') : value;
    const groups = (Array.isArray(raw) ? raw : [])
      .map((v) => Number(v))
      .filter((id) => !Number.isNaN(id));
    setForm((prev) => ({ ...prev, groups }));
  };

  const validate = () => {
    const errors = {};
    let valid = true;

    if (!form.username) {
      errors.username = 'Required';
      valid = false;
    }

    if (!isEditMode && (!form.password || form.password.length < 6)) {
      errors.password = 'Min 6 chars';
      valid = false;
    }
    if (isEditMode && form.password && form.password.length < 6) {
      errors.password = 'Min 6 chars';
      valid = false;
    }

    const mobile = validateMobileNumberFormat(form.mobile_num, true);
    if (!mobile.isValid) {
      errors.mobile_num = mobile.error;
      valid = false;
    }

    if (!form.groups.length) {
      errors.groups = 'Select at least one group';
      valid = false;
    }

    setFormErrors(errors);
    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    setSubmitDisable(true);

    const payload = { ...form, company_id: null };
    payload.groups = (Array.isArray(form.groups) ? form.groups : [])
      .map((v) => Number(v))
      .filter((id) => !Number.isNaN(id));
    if (isEditMode && !payload.password) {
      delete payload.password;
    }

    const submitRequest = isEditMode
      ? putRequest(`${PUT_URL.user.api}${editUserId}/`, payload, props)
      : postRequest(POST_URL.user.api, payload, props);

    submitRequest
      .then(res => {
        setSubmitDisable(false);
        if (res?.status === 200) {
          Swal.fire({
            position: 'top-end',
            type: 'success',
            title: 'Success',
            text: isEditMode ? 'User updated' : 'User created',
            showConfirmButton: false,
            timer: 1500,
          });
          navigate('/user/list'); 
        }
      })
      .catch(() => setSubmitDisable(false));
  };

  const sectionTitleSx = {
    mb: 2.5,
    pl: 1.75,
    borderLeft: '4px solid #4680FF',
    color: '#4680FF',
    fontWeight: 600,
    fontSize: { xs: '1rem', sm: '1.05rem' },
    lineHeight: 1.35,
  };

  const sectionPaperSx = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
  };

  return (
    <Box p={2}>

      {/* HEADER */}
      <Grid container alignItems="center" spacing={2}>
        <Grid item md={6} xs={12} mt={2}>
          <Typography variant="h5" fontWeight={600}>
            {isEditMode ? 'Edit User' : 'User Informations'}
          </Typography>
        </Grid>
        <Grid item md={6} xs={12}>
          <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => navigate(-1)}
              sx={{ textTransform: 'none' }}
            >
              <VisibilityOutlinedIcon sx={{ mr: 1 }} />
              View User
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Box mt={3} mb={3}>
        <Divider />
      </Box>

      {/* FORM */}
      <Box sx={sectionPaperSx}>
        <Typography variant="subtitle1" component="h2" sx={sectionTitleSx}>
          User Details
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
          <Typography variant="body2" color="text.secondary">
            Fill in the user information below
          </Typography>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>

            <Grid item xs={12} md={4}>
              <TextField
                label="Username"
                name="username"
                value={form.username}
                onChange={onChange}
                fullWidth
                error={!!formErrors.username}
                helperText={formErrors.username}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Password"
                name="password"
                type="password"
                value={form.password}
                onChange={onChange}
                fullWidth
                error={!!formErrors.password}
                helperText={formErrors.password || (isEditMode ? 'Leave blank to keep current password' : '')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Email"
                name="email"
                value={form.email}
                onChange={onChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Mobile"
                name="mobile_num"
                value={form.mobile_num}
                onChange={onChange}
                fullWidth
                error={!!formErrors.mobile_num}
                helperText={formErrors.mobile_num}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Is Staff</InputLabel>
                <Select
                  value={form.is_staff ? 'staff_yes' : 'staff_no'}
                  onChange={(e) =>
                    onChange({
                      target: { name: 'is_staff', value: e.target.value === 'staff_yes' },
                    })
                  }
                >
                  <MenuItem value="staff_no">No</MenuItem>
                  <MenuItem value="staff_yes">Yes</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!formErrors.groups}>
                <InputLabel>User Groups</InputLabel>
                <Select
                  multiple
                  value={form.groups}
                  onChange={handleGroupsChange}
                  input={<OutlinedInput label="User Groups" />}
                  renderValue={(selected) =>
                    selected
                      .map((id) => groupsList.find((g) => Number(g.id) === Number(id))?.name)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {groupsList.map(group => (
                    <MenuItem key={group.id} value={group.id}>
                      <Checkbox checked={form.groups.map(Number).includes(Number(group.id))} />
                      <ListItemText primary={group.name} />
                    </MenuItem>
                  ))}
                </Select>

                {formErrors.groups && (
                  <Typography variant="caption" color="error">
                    {formErrors.groups}
                  </Typography>
                )}
              </FormControl>
            </Grid>

          </Grid>
        )}

        {/* ACTIONS */}
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={() => navigate('/user/list')}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitDisable}
          >
            {submitDisable ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update User' : 'Create User')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default UserAdd;